import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, KeyRound, Link2, PanelRight, X } from 'lucide-react';
import { categorizeDlr } from '@paradigm/permissions/utils/humanizeDlr';
import { STATES, deriveState, deriveBlocking, byUrgency, describeApp } from '../../utils/appState';
import { factsFor } from '../../utils/fleetFacts';
import { copyText } from '../../utils/clipboard';
import { appPagePath } from '../../devmap';
import './AppFleet.css';

const STAGES = Object.values(STATES).sort((a, b) => a.rank - b.rank);

/** Text was dragged out inside this row: the click that ends a drag is a selection, not a press. */
function selectingIn(row) {
  const sel = window.getSelection?.();
  if (!sel || sel.isCollapsed || !sel.toString().trim()) return false;
  return row.contains(sel.anchorNode) || row.contains(sel.focusNode);
}

/** 412 → "412", 2210 → "2.2k", 18000 → "18k". Unknown stays a dash, never a zero. */
export function compact(n) {
  if (n == null) return '—';
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

export function Face({ app, big }) {
  const initials = (app.name || '?').trim().slice(0, 2).toUpperCase();
  return app.logo_url
    ? <img className={`fleet__face${big ? ' is-big' : ''}`} src={app.logo_url} alt="" />
    : <span className={`fleet__face${big ? ' is-big' : ''}`} aria-hidden="true">{initials}</span>;
}

export function StagePill({ state, unknown = false }) {
  if (unknown) return <span className="fleet__stage" title="Could not tell yet">—</span>;
  return <span className={`fleet__stage is-${state.key}`}>{state.label}</span>;
}

/**
 * The Apps list: how many apps sit at each stage (a press filters to it), a
 * table of every app with the numbers that say how it is doing and the one
 * thing it needs next. A row opens its app in one click; its Next goes
 * straight to that step; the glance button at its end opens a side panel
 * with the app's standing, for looking without leaving the list. Stage and selection live in the URL (`?stage=`, `?open=`), so
 * a filtered list or an opened app can be linked to.
 *
 * Until the authorization counts are in (loading, or `/my-apps/overview`
 * failed: `signals.usersKnown` false), every rung above Designing is unknown,
 * and the list says so with a dash rather than filing every app under
 * Registered with "Nothing waiting".
 */
export default function AppFleet({ apps, signals, schemaNames = {} }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const stage = params.get('stage');
  const openId = params.get('open');

  const usersKnown = !!signals?.usersKnown;
  const rows = useMemo(() => apps.map((app) => {
    const facts = factsFor(app, signals);
    const state = deriveState(app, facts);
    return {
      app,
      facts,
      state,
      // Designing is read off the record; anything above it needs the counts.
      unknown: !usersKnown && state.key !== 'designing',
      blocker: deriveBlocking(app, facts),
      sortKey: describeApp(app, facts).sortKey,
      // Real people only — the number the stage is decided on. Test users sit beside it.
      people: facts.realUsers,
      testers: facts.testUsers,
      calls: signals?.callsByApp?.[String(app.id)],
    };
  }).sort(byUrgency), [apps, signals, usersKnown]);

  const counts = Object.fromEntries(STAGES.map((s) => [s.key, rows.filter((r) => r.state.key === s.key).length]));
  const shown = stage ? rows.filter((r) => r.state.key === stage) : rows;
  const open = rows.find((r) => String(r.app.id) === openId) || null;

  const goNext = (app, blocker) => (
    blocker.href ? navigate(blocker.href) : navigate(appPagePath(app.id, blocker.panel || 'overview'))
  );

  // A row behaves like the link it stands for: a press opens the app, a
  // Ctrl/Cmd-click or middle-click opens it in a new tab.
  const pressRow = (e, app) => {
    if (selectingIn(e.currentTarget)) return;
    const to = appPagePath(app.id, 'overview');
    if (e.metaKey || e.ctrlKey) window.open(to, '_blank', 'noopener');
    else navigate(to);
  };
  const middleRow = (e, app) => {
    // The row's own buttons and link handle themselves.
    if (e.button !== 1 || e.target.closest('a, button')) return;
    window.open(appPagePath(app.id, 'overview'), '_blank', 'noopener');
  };

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value == null) next.delete(key); else next.set(key, value);
    if (key === 'stage') next.delete('open');
    setParams(next);
  };

  return (
    <div className={`fleet${open ? ' has-peek' : ''}`}>
      <div className="fleet__main">
        <div className="fleet__stages" role="group" aria-label="Filter by stage">
          {STAGES.map((s) => (
            <button
              key={s.key}
              type="button"
              className="fleet__count"
              aria-pressed={stage === s.key}
              onClick={() => setParam('stage', stage === s.key ? null : s.key)}
            >
              <span className="fleet__count-label">{s.label}</span>
              <span className="fleet__count-n">{usersKnown || s.key === 'designing' ? counts[s.key] : '—'}</span>
            </button>
          ))}
        </div>

        <div className="fleet__table" role="table" aria-label="Apps">
          <div className="fleet__row fleet__row--head" role="row">
            <span role="columnheader">Name</span>
            <span role="columnheader">Stage</span>
            <span role="columnheader" className="is-num">Ver</span>
            <span role="columnheader" className="is-num">People</span>
            <span role="columnheader" className="is-num">Calls 30d</span>
            <span role="columnheader">Price</span>
            <span role="columnheader">Next</span>
            <span role="columnheader" aria-label="Glance" />
          </div>
          {shown.map(({ app, state, unknown, blocker, people, testers, calls }) => (
            // The whole row opens the app; the name is the link a keyboard reaches.
            <div
              key={app.id}
              role="row"
              className="fleet__row is-link"
              aria-current={open && open.app.id === app.id ? 'true' : undefined}
              onClick={(e) => pressRow(e, app)}
              onAuxClick={(e) => middleRow(e, app)}
            >
              <span role="cell" className="fleet__who">
                <Face app={app} />
                <span className="fleet__names">
                  <Link
                    to={appPagePath(app.id, 'overview')}
                    className="fleet__name"
                    onClick={(e) => e.stopPropagation()}
                  >{app.name}</Link>
                  <span className="fleet__sub">{app.deployment_mode === 'public' ? 'Public' : 'Incubator'}</span>
                </span>
              </span>
              <span role="cell"><StagePill state={state} unknown={unknown} /></span>
              <span role="cell" className="is-num is-mono">{app.client_id ? `v${app.spec_version || 1}` : '—'}</span>
              <span role="cell" className="is-num">
                {compact(people)}
                {testers > 0 && <span className="fleet__testers"> · {testers} test</span>}
              </span>
              <span role="cell" className="is-num">{compact(calls)}</span>
              <span role="cell">{app.pricing_display?.headline || 'Free'}</span>
              <span role="cell" className={blocker ? 'fleet__next' : 'fleet__next is-quiet'}>
                {blocker ? (
                  <button
                    type="button"
                    className="fleet__next-go"
                    onClick={(e) => { e.stopPropagation(); goNext(app, blocker); }}
                  >{blocker.action}</button>
                ) : unknown ? '—' : 'Nothing waiting'}
              </span>
              <span role="cell" className="fleet__glance-cell">
                <button
                  type="button"
                  className="fleet__glance"
                  aria-label={`Glance at ${app.name}`}
                  aria-pressed={!!open && open.app.id === app.id}
                  title="Glance without opening"
                  onClick={(e) => {
                    e.stopPropagation();
                    setParam('open', open && open.app.id === app.id ? null : String(app.id));
                  }}
                ><PanelRight size={15} /></button>
              </span>
            </div>
          ))}
          {shown.length === 0 && <p className="fleet__empty">Nothing at this stage.</p>}
        </div>
        <p className="fleet__foot">{shown.length} shown</p>
      </div>

      {open && <Peek row={open} schemaNames={schemaNames} onClose={() => setParam('open', null)} />}
    </div>
  );
}

function Peek({ row, schemaNames, onClose }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(null);   // null | 'Copied' | 'Copy failed'
  const { app, state, unknown, blocker, people, testers, calls, facts } = row;
  const go = (page) => navigate(appPagePath(app.id, page));
  const version = app.spec_version || 1;
  const checks = facts.score?.checks;
  const asks = categorizeDlr(app.dlr_inputs || [], [], null, schemaNames).data;

  const copyLink = () => {
    const url = `${window.location.origin}/applications?open=${app.id}`;
    // Say "Copied" only once the clipboard took it (utils/clipboard.js).
    copyText(url).then((ok) => {
      setCopied(ok ? 'Copied' : 'Copy failed');
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <aside className="peek" aria-label={`${app.name}, at a glance`}>
      <div className="peek__bar">
        <span className="peek__tag">App · v{version}</span>
        <button type="button" className="peek__close" onClick={onClose} aria-label="Close"><X size={15} /></button>
      </div>

      <div className="peek__head">
        <Face app={app} big />
        <div className="peek__names">
          <h2 className="peek__title">{app.name}</h2>
          {app.description && <p className="peek__desc">{app.description}</p>}
          <span className="peek__pills">
            <StagePill state={state} unknown={unknown} />
            <span className="fleet__stage">{app.deployment_mode === 'public' ? 'Public' : 'Incubator'}</span>
          </span>
        </div>
      </div>

      <div className="peek__actions">
        <button type="button" className="peek__act is-primary" onClick={() => go('overview')}><ArrowRight size={14} />Open</button>
        <button type="button" className="peek__act" onClick={() => go('keys')}><KeyRound size={14} />Keys</button>
        <button type="button" className="peek__act" onClick={copyLink}><Link2 size={14} />{copied || 'Link'}</button>
      </div>

      {blocker && (
        <section className="peek__band">
          <h3 className="peek__cap">Next</h3>
          <button
            type="button"
            className="peek__door"
            onClick={() => (blocker.href ? navigate(blocker.href) : go(blocker.panel || 'overview'))}
          >
            <span>{blocker.text}<span className="peek__door-act">{blocker.action}</span></span>
            <ArrowRight size={14} />
          </button>
        </section>
      )}

      <section className="peek__band">
        <h3 className="peek__cap">Standing</h3>
        <dl className="peek__facts">
          <div><dt>People</dt><dd>{people == null ? '—' : people.toLocaleString()}</dd></div>
          <div><dt>Test users</dt><dd>{testers == null ? '—' : testers.toLocaleString()}</dd></div>
          <div><dt>Calls, 30 days</dt><dd>{calls == null ? '—' : calls.toLocaleString()}</dd></div>
          <div><dt>Price</dt><dd>{app.pricing_display?.headline || 'Free'}</dd></div>
          <div><dt>Evals</dt><dd>{checks?.total ? `${checks.passed} of ${checks.total}` : '—'}</dd></div>
          <div><dt>Created</dt><dd>{app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}</dd></div>
        </dl>
      </section>

      <section className="peek__band">
        <h3 className="peek__cap">What it asks for<button type="button" className="peek__link" onClick={() => go('access')}>Edit</button></h3>
        {asks.length ? (
          <ul className="peek__asks">
            {asks.map((a) => (
              <li key={a.schema}>{a.schema}<span> · {a.verbs.map((v) => v.verb).join(', ')}</span></li>
            ))}
          </ul>
        ) : <p className="peek__note">Nothing yet. People cannot grant an app that asks for nothing.</p>}
      </section>

      <section className="peek__band">
        <h3 className="peek__cap">Related</h3>
        <div className="peek__doors">
          <button type="button" className="peek__door" onClick={() => go('keys')}><span>API keys</span><ArrowRight size={14} /></button>
          <button type="button" className="peek__door" onClick={() => go('testusers')}><span>Test users</span><ArrowRight size={14} /></button>
          <button type="button" className="peek__door" onClick={() => go('pricing')}><span>Price <span className="peek__door-sub">{app.pricing_display?.headline || 'Free'}</span></span><ArrowRight size={14} /></button>
          <button type="button" className="peek__door" onClick={() => go('versions')}><span>History <span className="peek__door-sub">v{version}</span></span><ArrowRight size={14} /></button>
        </div>
      </section>

      {app.client_id && (
        <section className="peek__band">
          <h3 className="peek__cap">About</h3>
          <dl className="peek__facts is-one"><div><dt>Client ID</dt><dd className="is-mono">{app.client_id}</dd></div></dl>
        </section>
      )}
    </aside>
  );
}
